/**
 * Every Auth0 application that is GuestMaker.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * GuestMaker is not one client. It is the web app AND the iOS operator app,
 * and each has its own `client_id`. The per-client configuration lived in two
 * independent maps — `clientThemes.ts` and `clientRedirects.ts` — each listing
 * the web client by hand, and a second GuestMaker client was added to NEITHER.
 *
 * What that looked like on a phone, the first time the iOS app signed in:
 *
 *   · the login screen wore the HOTELINKING mark on a plain white page,
 *     under the words "Log in to continue to GuestMaker Operator (iOS)" —
 *     the app name resolved and the brand did not
 *   · none of the GuestMaker shell: no purple header, no heart-and-M, no
 *     "Welcome back", no "Sign in to your Guestmaker workspace"
 *   · and a password reset from that app would have bounced the operator to
 *     `admin.hotelinking.com/login`, a product they do not use
 *
 * The login screen is where somebody decides whether they are in the right
 * place before typing a password. A mark they do not recognise there is the
 * single worst place for a branding gap.
 *
 * ⚠️ SO THE CLIENTS ARE LISTED ONCE, HERE, and both maps derive from this.
 * Two hand-kept lists of the same fact drift, and the one nobody is looking at
 * is always the one that is wrong. Adding the next GuestMaker client — an
 * Android app, a desktop build — means adding it here and nowhere else.
 */

/** The web application at guestmaker.ai. */
export const GUESTMAKER_WEB_CLIENT_ID = "cfEeG8R5eCto8s3F63fXH7vWwzJzU4Fo";

/**
 * The iOS operator app ("GuestMaker Operator (iOS)").
 *
 * Declared in `apps/native/eas.json` as `EXPO_PUBLIC_AUTH0_CLIENT_ID` in the
 * guestmaker repo. A public client id is not a secret — it ships inside every
 * build and is visible in the authorize URL.
 */
export const GUESTMAKER_IOS_CLIENT_ID = "t48cmuCjWEm30onw0LLS8tuNIRtFwqtf";

export const GUESTMAKER_CLIENT_IDS = [
  GUESTMAKER_WEB_CLIENT_ID,
  GUESTMAKER_IOS_CLIENT_ID,
] as const;

/**
 * Build a map entry per GuestMaker client.
 *
 * Both consumers call this rather than typing the ids again, so a client that
 * is in one map is necessarily in the other.
 */
export function forEveryGuestmakerClient<T>(value: T): Record<string, T> {
  return Object.fromEntries(GUESTMAKER_CLIENT_IDS.map((id) => [id, value]));
}
